export type JsonRecord = Record<string, unknown>;

export interface ApiDownload {
  blob: Blob;
  fileName: string;
}

export interface PaginatedList<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

let csrfToken = "";

export function setCsrfToken(token?: string) {
  csrfToken = token ?? "";
}

function extractErrorMessage(body: unknown, fallback: string) {
  if (typeof body === "object" && body !== null) {
    const record = body as JsonRecord;
    const nested =
      typeof record.error === "object" && record.error !== null
        ? (record.error as JsonRecord)
        : record;
    const details =
      typeof nested.details === "object" && nested.details !== null
        ? (nested.details as JsonRecord)
        : nested;
    if (typeof details.fields === "object" && details.fields !== null) {
      const messages = Object.values(details.fields).flatMap((value) =>
        Array.isArray(value)
          ? value.filter((message): message is string => typeof message === "string")
          : typeof value === "string"
            ? [value]
            : [],
      );
      if (messages.length) return messages.join("\n");
    }
    const message = record.message ?? record.error ?? record.detail;
    if (typeof message === "string" && message.trim()) return message;
    if (typeof message === "object" && message !== null) {
      const nestedMessage = (message as JsonRecord).message;
      if (typeof nestedMessage === "string" && nestedMessage.trim()) return nestedMessage;
    }
  }
  return fallback;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    method,
    headers,
    credentials: "include",
  });

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const body =
    response.status === 204 ? undefined : isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(
      extractErrorMessage(body, `Die Anfrage ist fehlgeschlagen (${response.status}).`),
      response.status,
      body,
    );
  }

  return body as T;
}

function downloadFileName(header: string | null) {
  const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ""));
    } catch {
      // Ein fehlerhafter optionaler Header verhindert den eigentlichen Download nicht.
    }
  }
  return header?.match(/filename="([^"]+)"/i)?.[1] ?? "download";
}

async function download(path: string, signal?: AbortSignal): Promise<ApiDownload> {
  const response = await fetch(`/api/v1${path}`, {
    method: "GET",
    headers: { Accept: "application/octet-stream" },
    credentials: "include",
    signal,
  });
  if (!response.ok) {
    const isJson = response.headers.get("content-type")?.includes("application/json");
    const body = isJson ? await response.json() : await response.text();
    throw new ApiError(
      extractErrorMessage(body, `Die Datei konnte nicht geladen werden (${response.status}).`),
      response.status,
      body,
    );
  }
  return {
    blob: await response.blob(),
    fileName: downloadFileName(response.headers.get("Content-Disposition")),
  };
}

export function saveApiDownload(downloaded: ApiDownload, fallbackFileName = "download") {
  const url = URL.createObjectURL(downloaded.blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = downloaded.fileName === "download" ? fallbackFileName : downloaded.fileName;
  anchor.hidden = true;
  window.document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const api = {
  get<T>(path: string, signal?: AbortSignal) {
    return request<T>(path, { signal });
  },
  post<T>(path: string, body?: unknown, signal?: AbortSignal) {
    return request<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  },
  patch<T>(path: string, body: unknown) {
    return request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
  },
  put<T>(path: string, body: unknown) {
    return request<T>(path, { method: "PUT", body: JSON.stringify(body) });
  },
  delete<T>(path: string, body?: unknown) {
    return request<T>(path, {
      method: "DELETE",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  },
  download,
};

export function normalizeList<T = JsonRecord>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object") {
    const record = payload as JsonRecord;
    if (Array.isArray(record.items)) return record.items as T[];
    if (Array.isArray(record.data)) return record.data as T[];
  }
  return [];
}

export function normalizePage<T = JsonRecord>(payload: unknown): PaginatedList<T> {
  const items = normalizeList<T>(payload);
  const record = asRecord(payload);
  const totalCount = readNumber(record, "totalCount", "total_count") || items.length;
  const pageSize = readNumber(record, "pageSize", "page_size") || Math.max(items.length, 1);
  const page = readNumber(record, "page") || 1;
  return {
    items,
    totalCount,
    page,
    pageSize,
    pageCount:
      readNumber(record, "pageCount", "page_count") ||
      (totalCount ? Math.ceil(totalCount / pageSize) : 0),
  };
}

export function pathWithQuery(
  path: string,
  values: Record<string, string | number | null | undefined>,
) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  }
  const suffix = query.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonRecord) : {};
}

export function readString(record: JsonRecord, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
  }
  return "";
}

export function readNumber(record: JsonRecord, ...keys: string[]): number {
  for (const key of keys) {
    const value = record[key];
    const divisor = /(?:Cents|_cents)$/i.test(key) ? 100 : 1;
    if (typeof value === "number" && Number.isFinite(value)) return value / divisor;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value)))
      return Number(value) / divisor;
  }
  return 0;
}

export function readBoolean(record: JsonRecord, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
    if (value === "true" || value === 1) return true;
    if (value === "false" || value === 0) return false;
  }
  return false;
}

export function entityId(record: JsonRecord): string {
  return readString(record, "id", "uuid", "publicId", "public_id");
}

export function formDataRecord(form: HTMLFormElement): Record<string, string | File> {
  return Object.fromEntries(
    [...new FormData(form).entries()].filter(
      ([, value]) => typeof value !== "string" || value.trim() !== "",
    ),
  );
}

/** Only changed values are sent; a deliberately cleared field is explicit null. */
export function formDataPatch(form: HTMLFormElement): Record<string, string | File | null> {
  const patch: Record<string, string | File | null> = {};
  for (const [name, value] of new FormData(form).entries()) {
    const control = form.elements.namedItem(name);
    let initial = "";
    if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)
      initial = control.defaultValue;
    else if (control instanceof HTMLSelectElement)
      initial =
        [...control.options].find((option) => option.defaultSelected)?.value ??
        control.options[0]?.value ??
        "";
    if (control instanceof HTMLElement && control.dataset.initialValue !== undefined)
      initial = control.dataset.initialValue;
    if (typeof value !== "string") {
      if (value.size) patch[name] = value;
      continue;
    }
    if (value !== initial) patch[name] = value.trim() === "" ? null : value;
  }
  return patch;
}
